import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { SERVICE_OPTIONS, VEHICLE_OPTIONS, IMAGES, estimateFare, routeInfo, TORONTO_LOCATIONS } from "../data";
import { QuoteForm } from "./QuoteForm";
import { AddressAutocomplete } from "./AddressAutocomplete";
import { MapPreview } from "./MapPreview";
import { useTrip } from "../context/TripContext";

const AIRPORTS = TORONTO_LOCATIONS.filter((l) => l.name.includes("Airport"));

const field = "w-full bg-[var(--nl-bg-3)] border border-white/10 px-4 py-3.5 text-white font-body placeholder:text-[var(--nl-muted)] focus:outline-none focus:border-gold focus:ring-1 focus:ring-[var(--nl-gold)] transition-colors duration-300";

const empty = {
  name: "", email: "", phone: "", service: SERVICE_OPTIONS[0], vehicle: VEHICLE_OPTIONS[0],
  pickup: "", dropoff: "", date: "", time: "", passengers: 1, notes: "",
  mode: "point_to_point", hours: 2, deposit: false,
};

export const BookingForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [rebooked, setRebooked] = useState(false);
  const [pickupPt, setPickupPt] = useState(null);   // { lat, lng } once a suggestion is chosen
  const [dropoffPt, setDropoffPt] = useState(null);
  const [km, setKm] = useState(null);
  const [durationMin, setDurationMin] = useState(null);
  const [routing, setRouting] = useState(false);
  const { setTrip } = useTrip();
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null); // {code,discount_type,value} | "invalid" | null

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("promo");
    if (p) {
      setPromoInput(p.toUpperCase());
      setTimeout(() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);

  useEffect(() => {
    const code = promoInput.trim();
    if (!code) { setPromo(null); return; }
    const t = setTimeout(() => {
      api.get(`/promos/validate?code=${encodeURIComponent(code)}`)
        .then((r) => setPromo(r.data.valid ? r.data : "invalid"))
        .catch(() => setPromo("invalid"));
    }, 450);
    return () => clearTimeout(t);
  }, [promoInput]);

  useEffect(() => {
    api.get("/availability").then((r) => setBlocks(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const raw = sessionStorage.getItem("nl_rebook");
    if (!raw) return;
    sessionStorage.removeItem("nl_rebook");
    try {
      const r = JSON.parse(raw);
      setForm((f) => ({ ...f, ...r, date: "", time: "", notes: "" }));
      if (r.pickup_lat != null && r.pickup_lng != null) setPickupPt({ lat: r.pickup_lat, lng: r.pickup_lng });
      if (r.dropoff_lat != null && r.dropoff_lng != null) setDropoffPt({ lat: r.dropoff_lat, lng: r.dropoff_lng });
      setRebooked(true);
      setTimeout(() => setRebooked(false), 4500);
      setTimeout(() => document.getElementById("book")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const apply = (v) => {
      if (v && VEHICLE_OPTIONS.includes(v)) setForm((f) => ({ ...f, vehicle: v }));
    };
    // On first mount (e.g. arriving from another page) read any stashed choice.
    const stashed = sessionStorage.getItem("nl_prefill_vehicle");
    if (stashed) { sessionStorage.removeItem("nl_prefill_vehicle"); apply(stashed); }
    // Live: clicking "Reserve this vehicle" on the same page dispatches this event.
    const onPick = (e) => apply(e.detail);
    window.addEventListener("nl-prefill-vehicle", onPick);
    return () => window.removeEventListener("nl-prefill-vehicle", onPick);
  }, []);

  // Compute real driving distance whenever both endpoints are geocoded (point-to-point only).
  useEffect(() => {
    if (form.mode !== "point_to_point") { setKm(null); return; }
    if (!pickupPt || !dropoffPt) { setKm(null); return; }
    let cancelled = false;
    setRouting(true);
    routeInfo(pickupPt, dropoffPt).then((r) => { if (!cancelled) { setKm(r.km); setDurationMin(r.min); setRouting(false); } });
    return () => { cancelled = true; };
  }, [pickupPt, dropoffPt, form.mode]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dateBlocked = React.useMemo(() => {
    if (!form.date) return false;
    return blocks.some((b) => {
      if (b.date !== form.date) return false;
      if (b.all_day) return true;
      if (form.time && b.start_time && b.end_time) return b.start_time <= form.time && form.time <= b.end_time;
      return false;
    });
  }, [blocks, form.date, form.time]);

  const estimate = useMemo(
    () => estimateFare({
      km, vehicle: form.vehicle,
      mode: form.mode, hours: form.hours, deposit: form.deposit,
    }),
    [km, form.vehicle, form.mode, form.hours, form.deposit]
  );

  const needsAddress = form.mode === "point_to_point" && (!pickupPt || !dropoffPt);

  // Promo discount is calculated off the FULL ride total (incl. HST).
  const discount = useMemo(() => {
    if (!estimate || !promo || promo === "invalid") return 0;
    const full = estimateFare({ km, vehicle: form.vehicle, mode: form.mode, hours: form.hours, deposit: false });
    if (!full) return 0;
    let d = promo.discount_type === "percent" ? (full.total * promo.value) / 100 : promo.value;
    d = Math.min(d, full.total - 0.5);
    return Math.max(0, Math.round(d * 100) / 100);
  }, [estimate, promo, km, form.vehicle, form.mode, form.hours]);

  const payNow = estimate ? (form.deposit ? estimate.total : Math.max(0, Math.round((estimate.total - discount) * 100) / 100)) : null;

  // Publish the live trip so each fleet card can show an estimated total.
  useEffect(() => {
    setTrip({ km, mode: form.mode, hours: Number(form.hours) || 2, deposit: form.deposit });
  }, [km, form.mode, form.hours, form.deposit, setTrip]);

  const submit = async (e) => {
    e.preventDefault();
    if (needsAddress) {
      toast.error("Please pick your pickup and drop-off from the address suggestions.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        ...form,
        passengers: Number(form.passengers),
        dropoff: form.mode === "hourly" ? (form.dropoff || "As directed") : form.dropoff,
        hours: form.mode === "hourly" ? Math.max(2, Number(form.hours) || 2) : null,
        pickup_lat: pickupPt?.lat ?? null,
        pickup_lng: pickupPt?.lng ?? null,
        dropoff_lat: form.mode === "hourly" ? null : (dropoffPt?.lat ?? null),
        dropoff_lng: form.mode === "hourly" ? null : (dropoffPt?.lng ?? null),
        promo_code: promoInput.trim() || undefined,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout. Try again.");
      setLoading(false);
    }
  };

  return (
    <section id="book" className="relative py-32 md:py-40 bg-[var(--nl-bg)] overflow-hidden" data-testid="booking-section">
      <div
        className="absolute inset-0 opacity-[0.07] bg-cover bg-center"
        style={{ backgroundImage: `url(${IMAGES.texture})` }}
      />
      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5"
          >
            <p className="label text-gold mb-6">Reserve</p>
            <h2 className="font-serif font-light text-5xl md:text-7xl tracking-tighter leading-[0.95] mb-8">
              Request your <span className="italic text-gold">chauffeur.</span>
            </h2>
            <p className="text-white/50 text-lg font-body font-light leading-relaxed mb-10">
              Tell us where and when. We'll confirm availability, the vehicle, and an all-inclusive quote — no hidden
              fees, no surge, ever.
            </p>
            <div className="space-y-4 border-t border-white/10 pt-8">
              <div className="flex justify-between"><span className="text-white/40 font-body">Response time</span><span className="text-gold font-body">Under 30 minutes</span></div>
              <div className="flex justify-between"><span className="text-white/40 font-body">Coverage</span><span className="text-white/80 font-body">Every city in Ontario</span></div>
              <div className="flex justify-between"><span className="text-white/40 font-body">Availability</span><span className="text-white/80 font-body">24 / 7 / 365</span></div>
            </div>
            <QuoteForm />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 border border-white/10 bg-[var(--nl-bg-2)] p-8 md:p-12"
          >
            {done ? (
              <div className="h-full flex flex-col items-start justify-center py-16" data-testid="booking-success">
                <span className="font-serif text-6xl text-gold mb-6">✓</span>
                <h3 className="font-serif text-4xl mb-4">Reservation received.</h3>
                <p className="text-white/50 font-body mb-8">A NamarckLimo concierge will be in touch shortly to confirm.</p>
                <button onClick={() => setDone(false)} className="label text-gold border-b border-gold/40 pb-1" data-testid="booking-new-btn">
                  Book another ride →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="booking-form">
                <div className="md:col-span-2 flex gap-2" data-testid="ride-type-toggle">
                  {[["point_to_point", "Point-to-point"], ["hourly", "Hourly · as directed"]].map(([val, lbl]) => (
                    <button
                      type="button"
                      key={val}
                      onClick={() => setForm((f) => ({ ...f, mode: val }))}
                      data-testid={`mode-${val}`}
                      className={`flex-1 label py-3 border transition-colors duration-300 ${form.mode === val ? "bg-gold text-black border-gold" : "border-white/15 text-white/60 hover:border-gold hover:text-gold"}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">Full Name</label>
                  <input required value={form.name} onChange={set("name")} className={field} placeholder="Jane Doe" data-testid="input-name" />
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">Phone</label>
                  <input required value={form.phone} onChange={set("phone")} className={field} placeholder="+1 (416) 000-0000" data-testid="input-phone" />
                </div>
                <div className="md:col-span-2">
                  <label className="label text-white/50 block mb-2">Email</label>
                  <input required type="email" value={form.email} onChange={set("email")} className={field} placeholder="jane@email.com" data-testid="input-email" />
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">Service</label>
                  <select value={form.service} onChange={set("service")} className={field} data-testid="select-service">
                    {SERVICE_OPTIONS.map((o) => <option key={o} value={o} className="bg-[var(--nl-bg-3)]">{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">Vehicle</label>
                  <select value={form.vehicle} onChange={set("vehicle")} className={field} data-testid="select-vehicle">
                    {VEHICLE_OPTIONS.map((o) => <option key={o} value={o} className="bg-[var(--nl-bg-3)]">{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">{form.mode === "hourly" ? "Start / Pickup Location" : "Pickup Location"}</label>
                  <AddressAutocomplete
                    required
                    value={form.pickup}
                    onChange={(v) => setForm((f) => ({ ...f, pickup: v }))}
                    onSelect={(sel) => setPickupPt(sel ? { lat: sel.lat, lng: sel.lng } : null)}
                    placeholder="Start typing an address…"
                    testid="input-pickup"
                  />
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="airport-quickpick">
                    <span className="label text-white/30 text-[10px] self-center">Airport:</span>
                    {AIRPORTS.map((a) => (
                      <button
                        type="button"
                        key={a.name}
                        onClick={() => { setForm((f) => ({ ...f, pickup: a.name })); setPickupPt({ lat: a.lat, lng: a.lng }); }}
                        className="label text-[10px] border px-2.5 py-1 text-[var(--nl-gold)] border-[var(--nl-border-gold)] hover:border-[var(--nl-gold)] transition-colors duration-300"
                        data-testid={`airport-${a.name.includes("YYZ") ? "yyz" : "ytz"}`}
                      >
                        {a.name.includes("YYZ") ? "Pearson (YYZ)" : "Billy Bishop (YTZ)"}
                      </button>
                    ))}
                  </div>
                </div>
                {form.mode === "hourly" ? (
                  <div>
                    <label className="label text-white/50 block mb-2">Hours (min 2)</label>
                    <input required type="number" min="2" max="12" value={form.hours} onChange={set("hours")} className={field} data-testid="input-hours" />
                  </div>
                ) : (
                  <div>
                    <label className="label text-white/50 block mb-2">Drop-off Location</label>
                    <AddressAutocomplete
                      required
                      value={form.dropoff}
                      onChange={(v) => setForm((f) => ({ ...f, dropoff: v }))}
                      onSelect={(sel) => setDropoffPt(sel ? { lat: sel.lat, lng: sel.lng } : null)}
                      placeholder="Start typing an address…"
                      testid="input-dropoff"
                    />
                  </div>
                )}
                {(pickupPt || (form.mode !== "hourly" && dropoffPt)) && (
                  <div className="md:col-span-2" data-testid="map-preview-wrap">
                    <MapPreview pickup={pickupPt} dropoff={form.mode === "hourly" ? null : dropoffPt} />
                  </div>
                )}
                <div>
                  <label className="label text-white/50 block mb-2">Date</label>
                  <input required type="date" value={form.date} onChange={set("date")} className={`${field} [color-scheme:dark]`} data-testid="input-date" />
                </div>
                <div>
                  <label className="label text-white/50 block mb-2">Time</label>
                  <input required type="time" value={form.time} onChange={set("time")} className={`${field} [color-scheme:dark]`} data-testid="input-time" />
                </div>
                <div className="md:col-span-2">
                  <label className="label text-white/50 block mb-2">Passengers</label>
                  <input required type="number" min="1" max="7" value={form.passengers} onChange={set("passengers")} className={field} data-testid="input-passengers" />
                </div>
                <div className="md:col-span-2">
                  <label className="label text-white/50 block mb-2">Notes (optional)</label>
                  <textarea value={form.notes} onChange={set("notes")} rows={3} className={field} placeholder="Flight number, child seats, special requests…" data-testid="input-notes" />
                </div>
                <label className="md:col-span-2 flex items-center gap-3 cursor-pointer select-none" data-testid="deposit-toggle">
                  <input type="checkbox" checked={form.deposit} onChange={(e) => setForm((f) => ({ ...f, deposit: e.target.checked }))} className="w-4 h-4 accent-[var(--nl-gold)]" data-testid="input-deposit" />
                  <span className="text-white/70 font-body text-sm">Hold a <span className="text-gold">25% deposit</span> only now instead of the full fare (ideal for big-ticket rides)</span>
                </label>
                <div className="md:col-span-2">
                  <label className="label text-white/50 block mb-2">Promo code (optional)</label>
                  <input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    className={field}
                    placeholder="Have a card? Enter your code"
                    data-testid="input-promo"
                  />
                  {promo === "invalid" && promoInput.trim() && (
                    <p className="text-red-400/80 text-xs font-body mt-1.5" data-testid="promo-invalid">That code isn't valid.</p>
                  )}
                  {promo && promo !== "invalid" && (
                    <p className="text-emerald-400 text-xs font-body mt-1.5" data-testid="promo-valid">
                      ✓ {promo.code} applied — {promo.discount_type === "percent" ? `${promo.value}% off` : `$${promo.value} off`}
                    </p>
                  )}
                </div>
                <div className={`md:col-span-2 border bg-[var(--nl-gold-subtle,rgba(212,175,55,0.06))] px-5 py-4 transition-all duration-500 ${rebooked ? "border-gold ring-2 ring-gold/50 shadow-[0_0_30px_rgba(212,175,55,0.25)]" : "border-gold/30"}`} data-testid="fare-estimate">
                  <div className="flex items-center justify-between mb-3">
                    <p className="label text-white/50">{rebooked ? "Your quote — confirm & reserve" : `Card Hold ${form.deposit ? "(deposit)" : ""} — charged after ride`}</p>
                    <p className="font-serif text-3xl text-gold" data-testid="fare-amount">
                      {payNow != null ? `$${payNow.toFixed(2)}` : "—"}
                    </p>
                  </div>
                  {estimate ? (
                    <div className="space-y-1 font-body text-xs text-white/45 border-t border-white/10 pt-3">
                      <div className="flex justify-between">
                        <span>{form.mode === "hourly" ? `${estimate.hours} hrs · ${form.vehicle}` : `${estimate.km} km · ${form.vehicle}`}{form.deposit ? " · 25% deposit" : ""}</span>
                        <span data-testid="fare-subtotal">${estimate.subtotal.toFixed(2)}</span>
                      </div>
                      {form.mode !== "hourly" && durationMin != null && (
                        <div className="flex justify-between text-gold/70" data-testid="fare-distance-time">
                          <span>Estimated trip</span>
                          <span>{estimate.km} km · ~{durationMin} min drive</span>
                        </div>
                      )}
                      <div className="flex justify-between"><span>HST (13%)</span><span data-testid="fare-hst">${estimate.hst.toFixed(2)}</span></div>
                      {discount > 0 && (
                        <div className="flex justify-between text-emerald-400" data-testid="fare-discount">
                          <span>Promo {promo.code}</span><span>−${discount.toFixed(2)}</span>
                        </div>
                      )}
                      {form.deposit && discount > 0 ? (
                        <div className="flex justify-between text-white/70 pt-1"><span>Deposit hold now</span><span>${estimate.total.toFixed(2)}</span></div>
                      ) : (
                        <div className="flex justify-between text-white/70 pt-1"><span>Authorized hold</span><span>${payNow.toFixed(2)}</span></div>
                      )}
                      {form.deposit && discount > 0 && (
                        <p className="text-emerald-400/80 pt-1" data-testid="promo-deposit-note">Your ${discount.toFixed(2)} discount comes off the balance after your ride.</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-white/40 text-xs font-body border-t border-white/10 pt-3" data-testid="fare-hint">
                      {routing ? "Calculating route…" : needsAddress ? "Choose your pickup & drop-off from the suggestions for a live quote" : "Enter your trip details for a live quote"}
                    </p>
                  )}
                </div>
                {dateBlocked && (
                  <p className="md:col-span-2 text-red-400 text-sm font-body border border-red-400/30 bg-red-400/5 px-4 py-3" data-testid="date-blocked-warning">
                    Sorry, that date/time isn't available. Please choose another slot.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={loading || dateBlocked || needsAddress || routing}
                  className="md:col-span-2 label bg-gold text-black px-8 py-4 mt-2 hover:bg-[var(--nl-gold-hover)] transition-colors duration-300 disabled:opacity-60"
                  data-testid="booking-submit-button"
                >
                  {loading ? "Redirecting to secure checkout…" : "Reserve & Authorize Card"}
                </button>
                <p className="md:col-span-2 text-white/35 text-xs font-body -mt-1">
                  Secure payment by Stripe. We place a temporary hold on your card and only charge the final fare once your ride is complete.
                </p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
